/**
 * Fingering size tiers used by `<FingeringOverlay>`. Each tier
 * describes the SVG rendering parameters for a single fingering
 * group:
 *
 *   - `fontSize` / `numHeight` — digit glyph metrics.
 *   - `spacing` — vertical distance between successive digits in the
 *     same subgroup; also drives the per-system canvas-expansion math
 *     (see `computeOverlayExpansion`).
 *   - `groupWidth` / `padY` / `borderRadius` — geometry of the
 *     single translucent pill rendered behind each subgroup (main +
 *     alt). The pill replaced the per-digit white circles that used
 *     to sit behind each number.
 *   - `gapBetween` — extra vertical space between the main pill and
 *     the alt pill on top of the natural gap from
 *     `spacing - numHeight - 2·padY`. Pure visual breathing room.
 *
 * Corner radius is always `groupWidth / 2` so the short sides of
 * the pill render as full semicircles regardless of height. For
 * single-digit (wide) pills SVG auto-clamps this at `min(w, h)/2`,
 * which produces a sideways capsule. For multi-digit (tall) pills
 * the radius is honored literally and the top/bottom ends round
 * off into clean caps.
 */
// Three-stop scale shared with the MobileApp (`FontSizeTier`) so a
// project rendered on either client lands on the same visual tier.
// The slider in the web toolbar maps directly to these three values.
export type FingeringSize = 'small' | 'medium' | 'large';

export interface FingeringSizeConfig {
  fontSize: number;
  spacing: number;
  groupWidth: number;
  numHeight: number;
  padY: number;
  borderRadius: number;
  /**
   * Extra vertical space inserted between the main pill and the alt
   * pill (on top of the natural gap that already falls out of
   * `spacing - numHeight - 2·padY`). Pure visual breathing room —
   * it shifts the alt pill and its digits together so they stay in
   * sync.
   */
  gapBetween: number;
}

/**
 * Display width (px) the tiers above are calibrated for — roughly the
 * desktop score column (`max-w-7xl` ≈ 1280px × the inner `w-11/12`).
 * At or above this width the digits render at their configured size;
 * below it the whole config scales down linearly so the fingering
 * digits shrink together with the score image (e.g. on phones/tablets).
 * Set a touch below the true column width so full-width desktop layouts
 * cap cleanly at scale 1.0 (unchanged from the original sizing).
 */
export const FINGERING_REFERENCE_WIDTH = 1100;

/**
 * Display-width → scale factor, capped at 1 (large screens keep the
 * configured size; only narrower ones shrink) and floored so digits
 * stay legible on very small screens.
 */
export const fingeringScaleForWidth = (displayWidth: number): number =>
  Math.min(1, Math.max(0.25, displayWidth / FINGERING_REFERENCE_WIDTH));

/**
 * Scale every metric in a tier uniformly. Scaling all fields by the
 * same factor keeps the pill geometry proportional to the digits, so
 * the overlay just shrinks/grows as one unit.
 */
export const scaleFingeringConfig = (
  config: FingeringSizeConfig,
  scale: number
): FingeringSizeConfig => ({
  fontSize: config.fontSize * scale,
  spacing: config.spacing * scale,
  groupWidth: config.groupWidth * scale,
  numHeight: config.numHeight * scale,
  padY: config.padY * scale,
  borderRadius: config.borderRadius * scale,
  gapBetween: config.gapBetween * scale,
});

export const FINGERING_SIZES: Record<FingeringSize, FingeringSizeConfig> = {
  small: {
    fontSize: 10,
    spacing: 11,
    groupWidth: 12,
    numHeight: 10,
    padY: 1,
    borderRadius: 12 / 2,
    gapBetween: 3,
  },
  // Same numbers as the pre-slider `default` tier — kept identical so
  // the perceived default stays the same after the rename. New users
  // land here.
  medium: {
    fontSize: 14,
    spacing: 14,
    groupWidth: 15,
    numHeight: 14,
    padY: 1,
    borderRadius: 15 / 2,
    gapBetween: 4,
  },
  large: {
    fontSize: 18,
    spacing: 20,
    groupWidth: 22,
    numHeight: 18,
    padY: 1,
    borderRadius: 22 / 2,
    gapBetween: 5,
  },
};
